import * as React from 'react'
import { styled, css } from 'next-yak'

import * as behaviours from '../../../behaviours'
import * as colors from '../../../constants/colors'
import * as S from '../../../styles'

import { Field } from './Field'
import { FieldStatus } from './FieldStatus'

export enum SymbolPosition {
  AHEAD,
  BEHIND,
}

export type MoneyFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> & {
  context?: string
  description?: string
  error?: React.ReactNode
  format?: 'small'
  help?: React.ReactNode
  /** @default String(Math.random()) */
  label?: React.ReactNode
  modifier?: string
  selected?: boolean
  success?: React.ReactNode
  symbol: React.ReactNode
  symbolPosition: SymbolPosition
}

function MoneyFieldComponent({
  children,
  className,
  context,
  description,
  error,
  format,
  help,
  id,
  label,
  modifier,
  selected,
  success,
  symbol,
  symbolPosition,
  ...inputProps
}: MoneyFieldProps): React.ReactElement<MoneyFieldProps> {
  const stableId = React.useId()
  const { classList, status } = behaviours.getStatusClassName(className, error, success)

  return (
    <MoneyFieldWrapper
      className={classList.filter(Boolean).join(' ')}
      context={context}
      $hasModifier={Boolean(modifier)}
      htmlFor={id ?? stableId}
      label={label}
      description={description}
      help={help}
      $format={format}
      $selected={selected}
      $symbolPosition={symbolPosition}
    >
      {children}

      <S.input.ComposedWrapper $status={status} $disabled={inputProps.disabled}>
        {modifier && (
          <Modifier $isInputEmpty={!Boolean(inputProps.value)}>
            <S.copy.S.regular>{modifier}</S.copy.S.regular>
          </Modifier>
        )}
        {symbol && (
          <S.input.Prefix>
            <S.copy.S.regular>{symbol}</S.copy.S.regular>
          </S.input.Prefix>
        )}
        <S.input.Input {...inputProps} inputMode="decimal" type="text" id={id ?? stableId} />
      </S.input.ComposedWrapper>

      {(error || success) && <FieldStatus error={error} success={success} />}
    </MoneyFieldWrapper>
  )
}

type MoneyFieldWrapperProps = {
  $format?: any
  $hasModifier: boolean
  $symbolPosition: SymbolPosition
  $selected?: boolean
}

export const MoneyFieldWrapper = styled(Field)<MoneyFieldWrapperProps>`
  width: 100%;

  ${S.field.Message} {
    margin-top: 4px;
  }

  ${S.input.ComposedWrapper} {
    border-radius: 2px;
    box-sizing: border-box;
    display: flex;
    height: 50px;
  }

  ${S.input.Prefix} {
    background: transparent;

    ${S.copy.S.regular} {
      color: inherit;
    }
  }

  ${S.input.Count} {
    margin-top: 4px;
    flex: 0 0 auto;
    margin-left: auto;
  }

  ${S.field.Title}, ${S.field.Description} {
    margin-bottom: 7px;
  }

  ${S.input.Input} {
    width: 100%;
    padding: 14px 12px 16px 0;
  }

  ${({ $format }) => {
    if ($format === 'small') {
      return css`
        ${S.input.ComposedWrapper} {
          height: 34px;
        }

        ${S.input.Input} {
          height: 30px;
          padding: 12px 12px 12px 0;
        }

        ${S.input.Prefix} {
          height: 32px;
        }
      `
    }
  }};

  ${({ $format, $hasModifier = false, $symbolPosition }) => {
    if ($format === 'small' && $symbolPosition === SymbolPosition.BEHIND) {
      return css`
        ${S.input.Prefix} {
          order: ${() => ($hasModifier ? 2 : 1)};
          padding: 0 12px 0 ${() => ($hasModifier ? 0 : 8)}px;
        }

        ${S.input.Input} {
          padding: 6px 0 6px ${() => ($hasModifier ? 4 : 12)}px;
        }
      `
    } else if ($symbolPosition === SymbolPosition.BEHIND) {
      return css`
        ${S.input.Prefix} {
          order: ${() => ($hasModifier ? 2 : 1)};
          padding: 0 12px 0 8px;
        }

        ${S.input.Input} {
          padding: 12px 0 16px ${() => ($hasModifier ? 4 : 12)}px;
        }
      `
    } else if ($symbolPosition === SymbolPosition.AHEAD && $hasModifier) {
      return css`
        ${S.input.Prefix} {
          padding: 0 4px;
        }
      `
    }
  }};

  ${({ $selected, theme }) => {
    if ($selected) {
      return css`
        ${S.input.ComposedWrapper} {
          border-color: ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
        }
      `
    }
  }};
`

const Modifier = styled.span<{ $isInputEmpty: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;
  order: 0;
  padding: 0 0 2px 12px;

  ${({ $isInputEmpty }) => {
    if ($isInputEmpty) {
      return css`
        ${S.copy.S.regular} {
          color: ${colors.PRIMARY_SAND_500};
        }
      `
    } else {
      return css`
        ${S.copy.S.regular} {
          color: ${colors.PRIMARY_GREY_900};
        }
      `
    }
  }}
`

/** will forward any other props to the underlying `<input>` */
export const MoneyField = styled(MoneyFieldComponent)<MoneyFieldProps>``
MoneyField.displayName = 'MoneyField'
